/**
 * Dev-only sandbox for `<BulletproofContextMenu>`.
 *
 * Reachable at `/_sandbox/context-menu` (no auth gate, no route in nav).
 * Used by Phase 178.5.F smoke tests to verify:
 *   • Desktop right-click triggers the menu at the cursor
 *   • Mobile long-press (350ms) triggers at touch position
 *   • Keyboard Shift+F10 triggers from the focused row
 *   • Portal positioning + viewport collision detection
 *   • Variant color hierarchy (neutral / success / warning / danger)
 *   • Outside-click + Esc close the menu
 *   • onMenuOpen / onActionSelected / onCancelled analytics callbacks fire
 *
 * Once Phase 178.5.G migrates real surfaces (Booking Inbox, Slot
 * Calendar, Player table), this sandbox stays as a regression smoke surface.
 */
import { useState } from 'react';
import { BulletproofContextMenu } from '@/components/ui/bulletproof-context-menu';
import {
  Check, X, Eye, MessageSquare, RotateCcw, Lock, Calendar, AlertTriangle,
} from 'lucide-react';

const SAMPLE_ROWS = [
  { id: 'b_1', player: 'Anil G',     court: 'Court A', time: '18:00 - 19:00', status: 'pending', amount: 1200 },
  { id: 'b_2', player: 'Suresh K',   court: 'Court B', time: '19:00 - 20:00', status: 'pending', amount: 1500 },
  { id: 'b_3', player: 'Vikram R',   court: 'Court A', time: '20:00 - 21:00', status: 'confirmed', amount: 1200 },
  { id: 'b_4', player: 'Manoj P',    court: 'Court C', time: '21:00 - 22:00', status: 'pending', amount: 1800 },
];

export default function ContextMenuSandbox() {
  const [log, setLog] = useState([]);
  const pushLog = (line) => setLog((l) => [...l.slice(-9), `${new Date().toLocaleTimeString()} ${line}`]);

  const buildItems = (row) => [
    { label: 'View booking', icon: Eye, action: () => pushLog(`view ${row.id}`), variant: 'neutral' },
    { label: 'Message player', icon: MessageSquare, action: () => pushLog(`msg ${row.id}`), variant: 'neutral' },
    { separator: true },
    { label: 'Accept', icon: Check, action: () => pushLog(`✓ accept ${row.id}`), variant: 'success', disabled: row.status === 'confirmed' },
    { label: 'Block slot', icon: Lock, action: () => pushLog(`⚠ block ${row.id}`), variant: 'warning' },
    { separator: true },
    { label: 'Refund', icon: RotateCcw, action: () => pushLog(`✗ refund ${row.id}`), variant: 'danger' },
    { label: 'Mark no-show', icon: AlertTriangle, action: () => pushLog(`no-show ${row.id}`), variant: 'danger' },
  ];

  return (
    <div style={{ minHeight: '100vh', padding: 40, background: '#f8fafc', fontFamily: 'system-ui' }}>
      <h1 style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }} data-testid="sandbox-title">
        Bulletproof Context Menu — Sandbox
      </h1>
      <p style={{ color: '#64748b', marginBottom: 24, fontSize: 14 }}>
        Right-click any row. On mobile, long-press for 350ms. Keyboard users: focus a row and press Shift+F10.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'flex-start' }}>
        {/* ── Rows ───────────────────────────────────────────────── */}
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', background: '#f1f5f9', fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#475569' }}>
            Booking inbox (sample)
          </div>
          {SAMPLE_ROWS.map((row, idx) => (
            <BulletproofContextMenu
              key={row.id}
              metadata={{ entity_type: 'booking', row_id: row.id, operator_role: 'venue_owner' }}
              items={buildItems(row)}
              onMenuOpen={(m) => pushLog(`open menu: ${m.entity_type}/${m.row_id}`)}
              onActionSelected={(item, m) => pushLog(`selected "${item.label}" on ${m.row_id}`)}
              onCancelled={(m) => pushLog(`cancelled menu on ${m.row_id}`)}
            >
              <div
                data-testid={`sandbox-row-${idx}`}
                style={{
                  padding: '14px 16px',
                  display: 'grid',
                  gridTemplateColumns: '140px 1fr 140px 100px',
                  alignItems: 'center',
                  fontSize: 13,
                  borderTop: idx > 0 ? '1px solid #e2e8f0' : 'none',
                  cursor: 'context-menu',
                  background: 'white',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                }}
              >
                <span style={{ fontWeight: 600 }}>{row.player}</span>
                <span style={{ color: '#64748b' }}>{row.court} · {row.time}</span>
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.15em',
                  color: row.status === 'confirmed' ? '#10b981' : '#f59e0b',
                }}>
                  {row.status}
                </span>
                <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>₹{row.amount}</span>
              </div>
            </BulletproofContextMenu>
          ))}
        </div>

        {/* ── Analytics log ─────────────────────────────────────── */}
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 16 }} data-testid="sandbox-log">
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#475569', marginBottom: 8 }}>
            Analytics stream
          </div>
          {log.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>No events yet. Right-click a row.</div>
          ) : (
            log.map((line, i) => (
              <div key={i} style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#334155', padding: '2px 0' }}>
                {line}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edge-case demo: row near the bottom right corner — proves collision detection */}
      <div style={{ marginTop: 40, padding: 24, background: 'white', borderRadius: 12, border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#475569', marginBottom: 12 }}>
          Edge-case test row (collision detection)
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <BulletproofContextMenu
            metadata={{ entity_type: 'edge_case', row_id: 'corner' }}
            items={buildItems({ id: 'corner', status: 'pending' })}
            onMenuOpen={(m) => pushLog(`open menu: edge-case`)}
            onActionSelected={(item, m) => pushLog(`edge select "${item.label}"`)}
          >
            <button
              type="button"
              data-testid="sandbox-corner-row"
              style={{
                padding: '12px 20px',
                background: '#0ea5e9',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 13,
                cursor: 'context-menu',
              }}
            >
              Right-click me (anchored to right edge → menu must flip left)
            </button>
          </BulletproofContextMenu>
        </div>
      </div>
    </div>
  );
}
